/**
 * PostHog analytics wrapper.
 * Centralised event tracking for the VitaAdvisor conversion funnel.
 * API key & host come from env variables (REACT_APP_POSTHOG_KEY, REACT_APP_POSTHOG_HOST).
 *
 * Every named event in `track.*` is ALSO mirrored to the unified
 * tracking facade (`lib/tracking.js`) so GA4 and any future pixel
 * automatically receive the same canonical event without per-call wiring.
 */
import posthog from "posthog-js";
import { trackEvent as fanout } from "./tracking";

// GA4 canonical event-name map. Anything not listed passes through unchanged
// (the snake_case event name itself becomes the GA4 event).
const GA_NAME_MAP = {
  checkout_start: "checkout_started",
  purchase_success: "purchase_completed",
  assessment_start: "assessment_started",
  // assessment_completed, register_completed, login_success → identical in GA4
};

function mirrorToFanout(name, props) {
  try {
    const mapped = GA_NAME_MAP[name] || name;
    fanout(mapped, props || {});
  } catch { /* swallow */ }
}

let initialized = false;

export function initAnalytics() {
  if (initialized) return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
  if (!key) return;
  try {
    posthog.init(key, {
      api_host: host,
      autocapture: false,
      capture_pageview: false, // we manage pageviews manually with React Router
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      person_profiles: "identified_only",
    });
    initialized = true;
    // Expose for debugging / verification in dev tools
    if (typeof window !== "undefined") {
      window.posthog = posthog;
    }
  } catch {
    /* ignore — analytics must never break the app */
  }
}

export function capture(event, props = {}) {
  try {
    // Mirror to the unified fanout BEFORE the consent gate so the facade
    // can apply its own consent check (this lets GA4/etc still fire even
    // if PostHog itself failed to initialise for some reason).
    mirrorToFanout(event, props);
    _capturePosthogOnly(event, props);
  } catch {
    /* no-op */
  }
}

/**
 * Posthog-only capture (no fanout). Used by the unified `lib/tracking.js`
 * facade so events that came IN via fanout don't bounce back.
 */
export function _capturePosthogOnly(event, props = {}) {
  try {
    if (!initialized) return;
    posthog.capture(event, { ...props, ts: new Date().toISOString() });
  } catch {
    /* no-op */
  }
}

export function identify(user) {
  try {
    if (!initialized || !user?.id) return;
    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role,
      behavior_type: user.behavior_type || undefined,
    });
  } catch {
    /* no-op */
  }
}

export function resetIdentity() {
  try {
    if (!initialized) return;
    posthog.reset();
  } catch {
    /* no-op */
  }
}

export function capturePageview(pathname, search) {
  capture("$pageview", { path: pathname, search });
}

// Convenience event helpers — each one is a named conversion point
export const track = {
  event: (name, props) => capture(name, props || {}),
  landingView: () => capture("landing_page_view"),
  assessmentStart: () => capture("assessment_start"),
  assessmentStep: (step) => capture("assessment_step_completed", { step }),
  assessmentCompleted: (data) =>
    capture("assessment_completed", {
      bmi: data?.bmi,
      bmi_category: data?.bmi_category,
      conditions_count: (data?.conditions || []).length,
      conditions: data?.conditions,
      goals: data?.goals,
      behavior_type: data?.behavior_type,
    }),
  whatsappClick: (source, productId) =>
    capture("whatsapp_click", { source, product_id: productId || null }),
  checkoutStart: (data) =>
    capture("checkout_start", {
      product_id: data?.product_id,
      product_name: data?.product_name,
      price_gbp: data?.price_gbp,
      mode: data?.mode,
      coupon_code: data?.coupon_code || null,
      discount_pct: data?.discount_pct || 0,
      ...getReelAttribution(),
      ...(data?.variants || {}),
    }),
  purchaseSuccess: (data) =>
    capture("purchase_success", {
      session_id: data?.session_id,
      product_id: data?.product_id,
      product_name: data?.product_name,
      amount_total_minor: data?.amount_total,
      currency: data?.currency,
      mode: data?.mode,
      ...getReelAttribution(),
      ...(data?.variants || {}),
    }),
  referralCopy: (code) => capture("referral_copy", { code }),
  referralShare: (code) => capture("referral_share", { code }),
  checkinCreated: (data) =>
    capture("checkin_created", {
      weight_kg: data?.weight_kg,
      energy: data?.energy,
      mood: data?.mood,
    }),
  day7NudgeShown: () => capture("day7_nudge_shown"),
  day7NudgeClicked: () => capture("day7_nudge_clicked"),
  registerCompleted: (hasReferral) =>
    capture("register_completed", { has_referral: !!hasReferral }),
  loginSuccess: () => capture("login_success"),

  // Reel / story conversion funnel
  // Impression: fires once per session per story (deduped via sessionStorage)
  // to avoid spamming PostHog as cards autoplay in/out of view.
  reelImpression: (story, position) => {
    if (!story?.id) return;
    try {
      const key = "fg_reel_impressions_v1";
      const seen = JSON.parse(sessionStorage.getItem(key) || "[]");
      if (seen.includes(story.id)) return;
      seen.push(story.id);
      sessionStorage.setItem(key, JSON.stringify(seen.slice(-50)));
    } catch {
      /* private mode — fire anyway */
    }
    capture("reel_impression", {
      story_id: story.id,
      story_name: story.name || null,
      condition: story.condition || null,
      position: typeof position === "number" ? position : null,
      device_type: detectDeviceType(),
    });
  },
  reelModalOpened: (story, position) =>
    capture("reel_modal_opened", {
      story_id: story?.id || null,
      story_name: story?.name || null,
      condition: story?.condition || null,
      position: typeof position === "number" ? position : null,
      products_count: Array.isArray(story?.products) ? story.products.length : 0,
      has_video: !!story?.video_url,
      device_type: detectDeviceType(),
    }),
  reelProductClicked: (story, product, position, ctaText) => {
    // Stash attribution so a downstream checkout_start / purchase_success
    // can be tied back to the originating reel even though those events
    // fire from /report or /checkout-success.
    setReelAttribution({
      story_id: story?.id || null,
      story_name: story?.name || null,
      product_id: product?.id || null,
      position: typeof position === "number" ? position : null,
      ts: new Date().toISOString(),
    });
    capture("reel_product_clicked", {
      story_id: story?.id || null,
      story_name: story?.name || null,
      condition: story?.condition || null,
      product_id: product?.id || null,
      product_name: product?.name || null,
      position: typeof position === "number" ? position : null,
      cta_text: ctaText || null,
      device_type: detectDeviceType(),
    });
  },
};

// ---------- Attribution helpers ---------- //

const REEL_ATTR_KEY = "fg_reel_attribution_v1";
const REEL_ATTR_TTL_MS = 30 * 60 * 1000; // 30 minutes — long enough to span checkout

export function setReelAttribution(payload) {
  try {
    sessionStorage.setItem(REEL_ATTR_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/**
 * Returns the most recent reel attribution if one exists and is < 30 min old.
 * Spread into checkout_start / purchase_success / add_to_cart event props
 * to attribute the conversion back to the originating story.
 *
 * Example:
 *   track.checkoutStart({ ...productData, ...getReelAttribution() })
 */
export function getReelAttribution() {
  try {
    const raw = sessionStorage.getItem(REEL_ATTR_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data?.ts && Date.now() - new Date(data.ts).getTime() > REEL_ATTR_TTL_MS) {
      sessionStorage.removeItem(REEL_ATTR_KEY);
      return {};
    }
    return {
      reel_story_id: data.story_id || null,
      reel_story_name: data.story_name || null,
      reel_product_id: data.product_id || null,
      reel_position: typeof data.position === "number" ? data.position : null,
    };
  } catch {
    return {};
  }
}

function detectDeviceType() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/Tablet|iPad/.test(ua)) return "tablet";
  if (/Mobile|Android|iPhone/.test(ua)) return "mobile";
  return "desktop";
}
